import { Link } from "react-router-dom";
import { motion, type Variants } from "framer-motion";
import { STORE_CONTACT } from "@/constants/contact";

interface Chapter {
  num: string;
  title: string;
  body: string;
  cta?: React.ReactNode;
}

const chapters: Chapter[] = [
  {
    num: "01",
    title: "BUILT FOR SCALE",
    body: "Founded in 2019, VG Enterprise has rapidly grown into one of Laos's fastest-expanding import and distribution companies, recognized for its strong standards in quality, reliability, and large-scale supply chain execution. The company specializes in premium international consumer goods sourced from Europe, the United Kingdom, Dubai, South Africa, the United States, and other leading global markets, building a diverse portfolio of trusted, world-class brands. Today, VG Enterprise serves as a key supply partner to more than 50 major supermarkets nationwide, playing an important role in strengthening and modernizing Laos's retail distribution network through consistent service, efficient logistics, and a commitment to product excellence.",
  },
  {
    num: "02",
    title: "CONNECTED GROWTH",
    body: "What sets VG Enterprise apart is its long-term strategic vision and integrated growth model. Through its expansion into retail via its sister company VG Mart, the organization is creating a fully connected ecosystem that bridges global sourcing with direct consumer access in Laos. This approach is supported by strong international partnerships and a carefully curated range of globally recognized products from the United States and other leading regions, ensuring retailers and consumers have access to high-quality, in-demand goods. As VG Enterprise continues to scale, it remains focused on building stronger market connections, expanding its brand portfolio, and delivering greater value across the region.",
  },
  {
    num: "03",
    title: "PARTNER WITH US",
    body: "Connect with VG Enterprise to explore our full range of products and partnerships. Visit our website and follow us on Facebook to stay updated on our latest offerings and discover how we are bringing the world's leading brands directly to Laos.",
    cta: (
      <div className="mt-6 flex flex-wrap gap-3">
        <Link
          to="/products"
          className="btn-invert inline-flex bg-vg-navy text-white px-5 py-2.5 font-mono-acc uppercase tracking-[0.2em] text-xs hover:bg-vg-cyan hover:text-white"
        >
          Explore products
        </Link>
        <a
          href={STORE_CONTACT.facebookUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center border border-black/15 px-5 py-2.5 font-mono-acc uppercase tracking-[0.2em] text-xs text-vg-ink hover:bg-black/5 transition-colors"
        >
          Follow on Facebook
        </a>
      </div>
    ),
  },
];

const reveal: Variants = {
  hidden: { opacity: 0, y: 40 },
  show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] } },
};

export const Manifesto = () => (
  <section id="about" className="pt-12 lg:pt-16 pb-24 lg:pb-32 bg-[#f9fafb] border-y border-black/10" data-testid="about-section">
    <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="mb-20"
      >
        <span className="font-mono-acc uppercase tracking-[0.2em] text-xs text-vg-gray">02 / About VG Enterprise</span>
        <h2 className="font-heading font-black tracking-tighter text-3xl sm:text-5xl md:text-7xl mt-4 leading-[0.95]">
          <span className="block">GLOBAL BRANDS,</span>
          <span className="block">
            <span className="text-vg-cyan">LOCAL</span> ACCESS.
          </span>
        </h2>
      </motion.div>

      <motion.div
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.15 } } }}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="grid md:grid-cols-3 gap-12"
      >
        {chapters.map((c) => (
          <motion.article key={c.num} variants={reveal} className="border-t border-black/15 pt-8" data-testid={`about-chapter-${c.num}`}>
            <div className="font-heading font-black text-7xl md:text-8xl text-outline-num leading-none">{c.num}</div>
            <h3 className="font-heading font-black text-xl md:text-2xl tracking-tighter mt-6">{c.title}</h3>
            <p className="text-vg-gray leading-relaxed mt-4 text-sm md:text-base">{c.body}</p>
            {c.cta}
          </motion.article>
        ))}
      </motion.div>
    </div>
  </section>
);

export default Manifesto;
